import { forwardRef } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib"

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  onClearError?: () => void
  prefix?: string
  placeholder?: string
  hideSpinButtons?: boolean
}

export const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      label,
      error,
      className = "",
      onClearError,
      required,
      prefix,
      placeholder,
      id,
      hideSpinButtons,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="relative w-full flex flex-col gap-1.25">
        {label && (
          <Label
            htmlFor={id}
            className={cn(
              "text-xs leading-5 font-normal uppercase",
              error ? "text-brand-terracotta-bright" : "text-body",
            )}
          >
            {label}
            {required && (
              <span
                className={cn("ms-[-3px]", error ? "text-brand-terracotta-bright" : "text-body")}
              >
                *
              </span>
            )}
          </Label>
        )}
        <div className="relative w-full">
          {prefix && (
            <span className="absolute left-0 top-[33%] -translate-y-1/2 text-sm font-semibold text-body">
              {prefix}
            </span>
          )}
          <Input
            ref={ref}
            id={id}
            {...props}
            onFocus={e => {
              // Don't clear error on focus - this causes flickering
              props.onFocus?.(e)
            }}
            onBlur={e => {
              props.onBlur?.(e)
            }}
            onChange={e => {
              // Only clear error when user actually types something
              if (error && onClearError && e.target.value.length > 0) {
                onClearError()
              }
              props.onChange?.(e)
            }}
            className={cn(
              "border-0 border-b rounded-none shadow-none px-0 pt-0 pb-2.5 !h-7.5 text-sm font-semibold focus-visible:ring-0",
              error && "border-b border-brand-terracotta-bright text-brand-terracotta-bright",
              prefix && "pl-4",
              hideSpinButtons &&
                "[appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none",
              className,
            )}
            placeholder={placeholder || "Type here ..."}
          />
        </div>
        {error && <p className="text-xs text-brand-terracotta-bright">{error}</p>}
      </div>
    )
  },
)

CustomInput.displayName = "CustomInput"
